import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Grid,
  TablePagination,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  LocalPostOffice as PostalIcon,
  AttachMoney as MoneyIcon,
  DirectionsCar as CarIcon,
  AccessTime as TimeIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import TableToolbar from '../components/TableToolbar';
import { useAuth } from '../contexts/AuthContext';


const CompanyClients = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [postalCodeAutoFilled, setPostalCodeAutoFilled] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState('');
  const addressTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    client_company_name: '',
    office_address: '',
    postal_code: '',
    work_rate: '',
    mileage_rate: '',
    due_date_range: '0',
    clock_setting: '0',
    return_mileage: '0',
    company_id: '',
  });

  // Update formData when user is loaded
  useEffect(() => {

console.log("mder");
console.log(user);

console.log("mder1");
console.log(user.id);
    if (user && user.id) {
      setFormData(prev => ({
        ...prev,
        company_id: user.id
      }));
    }
  }, [user]);

  const fetchCompanies = async () => {
    if (!user || !user.id) {
      console.log('User or company_id not available yet');
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching companies with params:', {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        company_id: user.id
      });

      const token = localStorage.getItem('token');
      console.log('Auth token exists:', !!token);

      const response = await api.get('/v1/company-clients', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          company_id: user.id
        }
      });

      console.log('API Response:', response);

      if (response.data && response.data.data) {
        console.log('Setting companies:', response.data.data);
        setCompanies(response.data.data);
        setTotalItems(response.data.pagination.total);
      } else {
        console.log('Invalid response format:', response.data);
        setCompanies([]);
        setTotalItems(0);
        enqueueSnackbar('Error: Invalid response from server', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setCompanies([]);
      setTotalItems(0);
      enqueueSnackbar(
        error.response?.data?.message || 'Error fetching clients: ' + error.message, 
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      fetchCompanies();
    }
  }, [page, rowsPerPage, searchTerm, user]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addressTimeoutRef.current) {
        clearTimeout(addressTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenDialog = (company = null) => {
    if (!user || !user.id) {
      enqueueSnackbar('User information not available', { variant: 'error' });
      return;
    }

    // Reset address autocomplete states
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setPostalCodeAutoFilled(false);

    if (company) {
      setSelectedCompany(company);
      setFormData({
        first_name: company.first_name,
        last_name: company.last_name,
        email: company.email,
        phone: company.phone,
        client_company_name: company.client_company_name,
        office_address: company.office_address,
        postal_code: company.postal_code,
        work_rate: company.work_rate,
        mileage_rate: company.mileage_rate,
        due_date_range: company.due_date_range,
        clock_setting: company.clock_setting,
        return_mileage: company.return_mileage,
        company_id: user.id,
      });
    } else {
      setSelectedCompany(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        client_company_name: '',
        office_address: '',
        postal_code: '',
        work_rate: '',
        mileage_rate: '',
        due_date_range: '0',
        clock_setting: '0',
        return_mileage: '0',
        company_id: user.id,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCompany(null);
    setPostalCodeError(''); // Reset postal code error when dialog closes
    setPostalCodeAutoFilled(false); // Reset auto-filled flag
  };

  // US Postal Code validation function
  const validateUSPostalCode = (postalCode) => {
    if (!postalCode || postalCode.trim() === '') return { isValid: true, message: '' }; // Empty is OK for required field validation
    
    // Remove spaces and convert to uppercase
    const cleaned = postalCode.replace(/\s+/g, '').toUpperCase();
    
    // US ZIP code patterns:
    // 5 digits: 12345
    // 5 digits + hyphen + 4 digits (ZIP+4): 12345-6789
    const usZipPattern = /^(\d{5})(-\d{4})?$/;
    
    if (usZipPattern.test(cleaned)) {
      return { isValid: true, message: '' };
    } else {
      return { 
        isValid: false, 
        message: 'Invalid US postal code format. Use 12345 or 12345-6789' 
      };
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Validate US postal code when manually changed
    if (name === 'postal_code') {
      const validation = validateUSPostalCode(value);
      if (!validation.isValid && value.trim() !== '') {
        setPostalCodeError(validation.message);
        // Show validation warning but don't block input
        setTimeout(() => {
          enqueueSnackbar(validation.message, { variant: 'warning', autoHideDuration: 3000 });
        }, 500);
      } else {
        setPostalCodeError('');
      }
      // Reset auto-filled flag if user manually changes
      if (postalCodeAutoFilled) {
        setPostalCodeAutoFilled(false);
      }
    }
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      office_address: value
    }));

    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    if (value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    } 
    addressTimeoutRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/v1/employees/places/autocomplete?input=${encodeURIComponent(value)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await res.json();
        setAddressSuggestions(data.data.predictions || []);
        setShowSuggestions(true);
      } catch (err) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleAddressSelect = async (suggestion) => {
    try {
      // First update the address field
      setFormData(prev => ({
        ...prev,
        office_address: suggestion.description
      }));
      setAddressSuggestions([]);
      setShowSuggestions(false);

      // Then fetch detailed place information including postal code
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/employees/places/details?place_id=${suggestion.place_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const placeDetails = await response.json();
      
      if (placeDetails.data && placeDetails.data.postal_code) {
        // Validate US postal code format
        const postalCode = placeDetails.data.postal_code;
        const validation = validateUSPostalCode(postalCode);
        
        // Update postal code if available (update even if invalid, but show warning)
        setFormData(prev => ({
          ...prev,
          postal_code: postalCode
        }));
        setPostalCodeAutoFilled(true);
        
        if (validation.isValid) {
          setPostalCodeError('');
          enqueueSnackbar('Address and postal code updated automatically', { variant: 'success' });
        } else {
          setPostalCodeError(validation.message);
          enqueueSnackbar('Address updated. Postal code may need manual correction.', { variant: 'warning' });
        }
      } else {
        setPostalCodeAutoFilled(false);
        enqueueSnackbar('Address updated. Please enter postal code manually if needed.', { variant: 'info' });
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      // Still update the address even if postal code fetch fails
      setFormData(prev => ({
        ...prev,
        office_address: suggestion.description
      }));
      setAddressSuggestions([]);
      setShowSuggestions(false);
      enqueueSnackbar('Address updated. Please enter postal code manually.', { variant: 'warning' });
    }
  };

  const handleInputValidation = (e) => {
    if (!e.target.validity.valid) {
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !user.id) {
      enqueueSnackbar('User information not available', { variant: 'error' });
      return;
    }

    try {
      // Validate required fields
      const requiredFields = {
        first_name: 'First Name',
        last_name: 'Last Name',
        email: 'Email',
        phone: 'Phone',
        client_company_name: 'Company Name',
        office_address: 'Office Address',
        postal_code: 'Postal Code',
        work_rate: 'Hourly Rate',
        mileage_rate: 'Mileage Rate'
      };

      const missingFields = [];
      for (const [field, label] of Object.entries(requiredFields)) {
        if (!formData[field] || 
            (typeof formData[field] === 'string' && formData[field].trim() === '') ||
            (typeof formData[field] === 'number' && isNaN(formData[field]))) {
          missingFields.push(label);
        }
      }

      if (missingFields.length > 0) {
        enqueueSnackbar(`Please fill in the following required fields: ${missingFields.join(', ')}`, { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        enqueueSnackbar('Please enter a valid email address', { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
        return;
      }

      // US Contact Number Validation
      const usPhoneRegex = /^(?:\+1\s?)?\(?([2-9][0-9]{2})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
      if (!usPhoneRegex.test(formData.phone)) {
        enqueueSnackbar(
          'Invalid U.S. contact number.\nPlease enter a valid phone number in one of these formats:\n\n' +
          '• 2124567890\n' +
          '• (212) 456-7890\n' +
          '• 212-456-7890\n' +
          '• +1 212-456-7890',
          {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            autoHideDuration: 5000,
          }
        );
        return;
      }



      // Validate numeric fields
      const numericFields = {
        work_rate: 'Hourly Rate',
        mileage_rate: 'Mileage Rate'
      };

      for (const [field, label] of Object.entries(numericFields)) {
        const value = parseFloat(formData[field]);
        if (isNaN(value) || value < 0) {
          enqueueSnackbar(`${label} must be a valid positive number`, { 
            variant: 'error',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          });
          return;
        }
      }

      // Convert numeric fields to numbers if they're strings, with proper validation
      const updatedFormData = {
        ...formData,
        work_rate: parseFloat(formData.work_rate) || 0,
        mileage_rate: parseFloat(formData.mileage_rate) || 0,
        due_date_range: formData.due_date_range ? (parseInt(formData.due_date_range) || 0) : 0,
        clock_setting: formData.clock_setting ? (parseInt(formData.clock_setting) || 0) : 0,
        return_mileage: formData.return_mileage ? (parseInt(formData.return_mileage) || 0) : 0
      };

      // Ensure all string fields are properly sanitized
      const sanitizedFormData = {
        ...updatedFormData,
        first_name: (updatedFormData.first_name || '').trim(),
        last_name: (updatedFormData.last_name || '').trim(),
        email: (updatedFormData.email || '').trim(),
        phone: (updatedFormData.phone || '').trim(),
        client_company_name: (updatedFormData.client_company_name || '').trim(),
        office_address: (updatedFormData.office_address || '').trim(),
        postal_code: (updatedFormData.postal_code || '').trim(),
        company_id: updatedFormData.company_id || user.id
      };

      if (selectedCompany) {
        await api.put(`/v1/company-clients/${selectedCompany.id}`, sanitizedFormData);
        enqueueSnackbar('Client updated successfully', { variant: 'success' });
      } else {
        await api.post('/v1/company-clients', sanitizedFormData);
        enqueueSnackbar('Client created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchCompanies();
    } catch (error) {
      console.error('Error saving client:', error);
      
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        enqueueSnackbar(errorMessages, { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      } else if (error.response?.data?.message) {
        enqueueSnackbar(error.response.data.message, { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      } else {
        enqueueSnackbar('Error saving client. Please try again.', { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      }
    }
  };

  const handleDelete = async (id) => {
    if (!user || !user.id) {
      enqueueSnackbar('User information not available', { variant: 'error' });
      return;
    }

    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await api.delete(`/v1/company-clients/${id}`);
        enqueueSnackbar('Client deleted successfully', { variant: 'success' });
        fetchCompanies();
      } catch (error) {
        enqueueSnackbar('Error deleting client', { variant: 'error' });
      }
    }
  };

  const handleExport = async () => {
    if (!user || !user.id) {
      enqueueSnackbar('User information not available', { variant: 'error' });
      return;
    }

    try {
      const response = await api.get('/v1/company-clients/export', { 
        params: { search: searchTerm || '' },
        responseType: 'blob' 
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Error exporting clients', { variant: 'error' });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Show loading state if user is not loaded yet
  if (!user) {
    return (
      <Container maxWidth={false} sx={{ py: 2, px: '0 !important', width: { sm: `calc(100% )` }, marginLeft: '0 !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  } 

  return (
    <Container 
      maxWidth={false} 
      className="css-k008qs"
      sx={{ 
        py: { xs: 0, sm: 2 }, 
        px: { xs: 0, sm: '0 !important' }, 
        width: { sm: `calc(100%)` }, 
        backgroundColor: '#f5f5f5',
        minHeight: '100vh'
      }}
    >
      <TableToolbar title="Client Companies" />

      {/* SCROLLABLE SECTION: Search + Table + Pagination */}
      <Paper sx={{
        width: '100%',
        borderRadius: { xs: 0, sm: 1 },
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        p: { xs: 2, sm: 2 }
      }}>
        {/* Search bar */}
        <Box sx={{
          p: { xs: 0, sm: 2 },
          pb: { xs: 2, sm: 2 },
          borderBottom: '1px solid #e0e0e0',
          mb: { xs: 1, sm: 0 }
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel htmlFor="search">Search</InputLabel>
                <OutlinedInput
                  id="search"
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  label="Search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search clients..."
                />
              </FormControl>
            </Grid>
            <Grid 
              item 
              xs={12} 
              sm={12} 
              md={8} 
              lg={8} 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
                gap: { xs: 1, sm: 1 },
                mt: { xs: 0, sm: 0 }
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ 
                  backgroundColor: '#1a237e',
                  '&:hover': {
                    backgroundColor: '#0d47a1'
                  },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Add Company
              </Button>
              <Button
                variant="contained"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                sx={{
                  backgroundColor: '#1a237e',
                  '&:hover': {
                    backgroundColor: '#0d47a1'
                  },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer sx={{ 
          overflowX: { xs: 'auto', sm: 'hidden' },
          maxWidth: '100%'
        }}>
          <Table stickyHeader>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: '#4e0159',
                  color: '#ffffff'
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 0,
                  paddingRight: 1
                }}>S.No</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>First Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Last Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Email</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Phone</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Company Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Office Address</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Postal Code</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Created At</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Hourly Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Mileage Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Return Mileage</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Due Date Range</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 2
                }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : companies && companies.length > 0 ? (
                companies.map((company, index) => (
                  <TableRow 
                    key={company.id}
                    hover
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                    }}
                  >
                    <TableCell sx={{ paddingLeft: 0, paddingRight: 1 }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>{company.first_name}</TableCell>
                    <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>{company.last_name}</TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{company.email}</TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{company.phone}</TableCell>
                    <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>{company.client_company_name}</TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>
                      <Box sx={{ 
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {company.office_address}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{company.postal_code}</TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{new Date(company.created_at).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>${company.work_rate}</TableCell>
                    <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>${company.mileage_rate}</TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>
                      <Chip
                        label={company.return_mileage === 1 ? 'Yes' : 'No'}
                        color={company.return_mileage === 1 ? 'success' : 'error'}
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          minWidth: '60px',
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>Net {company.due_date_range}</TableCell>
                    <TableCell sx={{ paddingLeft: 1, paddingRight: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenDialog(company)}
                          sx={{
                            backgroundColor: '#1a237e',
                            '&:hover': {
                              backgroundColor: '#0d47a1'
                            }
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(company.id)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No clients found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #e0e0e0'
          }}
        />
      </Paper>

      {/* Dialog for Add/Edit Company */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 1 },
            m: { xs: 0, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2 }
        }}>
          {selectedCompany ? 'Edit Company Client' : 'Add Company Client'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ 
            pt: { xs: 2, sm: 3 }, 
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 }
          }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">First Name</Typography>
                </Box>
                <TextField
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Last Name</Typography>
                </Box>
                <TextField
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Email</Typography>
                </Box>
                <TextField
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Phone</Typography>
                </Box>
                <TextField
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Client Company Name</Typography>
                </Box>
                <TextField
                  name="client_company_name"
                  value={formData.client_company_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Office Address</Typography>
                </Box>
                <div style={{ position: 'relative' }}>
                  <TextField
                    name="office_address"
                    value={formData.office_address}
                    onChange={handleAddressChange}
                    fullWidth
                    required
                    multiline
                    rows={2}
                    size="small"
                    autoComplete="off"
                  />
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <ul style={{
                      position: 'absolute',
                      background: '#fff',
                      border: '1px solid #ccc',
                      width: '100%',
                      zIndex: 10,
                      listStyle: 'none',
                      margin: 0,
                      padding: 0,
                      maxHeight: 200,
                      overflowY: 'auto'
                    }}>
                      {addressSuggestions.map((s) => (
                        <li
                          key={s.place_id}
                          onClick={() => handleAddressSelect(s)}
                          style={{ padding: '8px', cursor: 'pointer' }}
                        >
                          {s.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PostalIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Postal Code</Typography>
                </Box>
                <TextField
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={!!postalCodeError}
                  helperText={postalCodeError || (postalCodeAutoFilled ? "Auto-filled from address selection" : "")}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: postalCodeError ? 'error.main' : (postalCodeAutoFilled ? 'success.main' : 'text.secondary')
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MoneyIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Hourly Rate</Typography>
                </Box>
                <TextField
                  name="work_rate"
                  type="number"
                  value={formData.work_rate}
                  onChange={handleChange}
                  onInput={handleInputValidation}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ 
                    min: 0,
                    step: "1"
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CarIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Mileage Rate</Typography>
                </Box>
                <TextField
                  name="mileage_rate"
                  type="number"
                  value={formData.mileage_rate}
                  onChange={handleChange}
                  onInput={handleInputValidation}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ 
                    min: 0,
                    step: "1"
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TimeIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Due Date Range</Typography>
                  <Tooltip title="This determines when payment is due from your client. N15 means Net 15 and so forth." placement="top">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    name="due_date_range"
                    value={formData.due_date_range}
                    onChange={handleChange}
                  >
                    <MenuItem value="0">Due on receipt</MenuItem>
                    <MenuItem value="15">Net 15</MenuItem>
                    <MenuItem value="30">Net 30</MenuItem>
                    <MenuItem value="45">Net 45</MenuItem>
                    <MenuItem value="60">Net 60</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CarIcon sx={{ mr: 1, color: '#4e0159' }} />
                  <Typography variant="subtitle2">Return Mileage</Typography>
                  <Tooltip title="Pick yes, if your client pays for return mileage." placement="top">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    name="return_mileage"
                    value={formData.return_mileage}
                    onChange={handleChange}
                  >
                    <MenuItem value="0">No</MenuItem>
                    <MenuItem value="1">Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 2 },
            px: { xs: 2, sm: 3 },
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Button 
              onClick={handleCloseDialog}
              fullWidth={isMobile}
              sx={{
                color: '#4e0159',
                '&:hover': {
                  color: '#4e0159',
                  opacity: 0.8,
                  backgroundColor: 'rgba(78, 1, 89, 0.1)'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              fullWidth={isMobile}
              sx={{
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1'
                }
              }}
            >
              {selectedCompany ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default CompanyClients; 